import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFamilyStatus } from '../context/FamilyContext';
import { Users, Tent, GraduationCap, ChevronRight, CheckCircle, Clock } from 'lucide-react';

const sections = [
  {
    id: 'family',
    emoji: '🏡',
    title: '1. My Family',
    subtitle: 'My spouse & children',
    desc: 'Manage your family members. Add your spouse and children to unlock the full portal experience.',
    href: '/my-family',
    gradient: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    icon: Users,
    color: 'sky',
  },
  {
    id: 'camps',
    emoji: '⛺',
    title: '2. My Camps',
    subtitle: 'Camp enrollments',
    desc: 'A. If you just ENROLLED in a camp, provide all the required information.\n\nB. If you have ATTENDED a camp, view your results and learning profiles.',
    href: '/my-camps',
    gradient: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    icon: Tent,
    color: 'amber',
  },
  {
    id: 'coaching',
    emoji: '📘',
    title: '3. Coaching/Mentorship',
    subtitle: 'SuperTeens Coaching - AI',
    desc: 'Welcome to the Coaching/Mentorship Classes. Watch all recorded lectures by Dr Ernest Wong, complete assignments and projects.',
    href: '/my-coaching',
    gradient: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    icon: GraduationCap,
    color: 'violet',
  },
];

export default function Dashboard() {
  const { userName, needOnboarding } = useAuth();
  const { isFamilySetupComplete } = useFamilyStatus();

  // Derive progress locally — no API needed
  const boolProgress = [
    { key: 'profile_complete', label: 'Profile',  done: !needOnboarding },
    { key: 'family_setup',     label: 'Family',   done: isFamilySetupComplete },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Welcome */}
        <div className="mb-10" style={{ animation: 'fadeIn 0.4s ease' }}>
          <p className="text-sky-500 text-xs font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
            <span className="w-6 h-px bg-sky-400 inline-block" />
            Dashboard
          </p>
          <h1
            data-testid="welcome-message"
            className="text-4xl sm:text-5xl font-bold text-slate-900 font-outfit"
          >
            Welcome back, {userName || 'there'}!
          </h1>
          <p className="text-slate-500 mt-2">Here's your coaching portal overview.</p>
        </div>

        {/* Progress Bar */}
        {boolProgress.length > 0 && (
          <div
            data-testid="progress-summary"
            className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-900">Your Progress</h2>
              <span className="text-xs text-slate-400 font-medium">
                {boolProgress.filter(p => p.done).length}/{boolProgress.length} completed
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 mb-4">
              <div
                className="bg-sky-500 h-2 rounded-full transition-all duration-700"
                style={{ width: `${(boolProgress.filter(p => p.done).length / boolProgress.length) * 100}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {boolProgress.map(({ key, label, done }) => (
                <span
                  key={key}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                    done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {done ? <CheckCircle size={12} /> : <Clock size={12} />}
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Section Cards */}
        <div data-testid="dashboard-sections" className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sections.map((s, i) => {
            const Icon = s.icon;
            const familyStatus = s.id === 'family' ? isFamilySetupComplete : null;
            return (
              <Link
                key={s.id}
                to={s.href}
                data-testid={`dashboard-section-${s.id}`}
                className="group bg-white rounded-3xl border border-slate-200/60 shadow-sm hover:-translate-y-1 hover:shadow-xl hover:border-sky-200 transition-all duration-300 overflow-hidden"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className={`h-1.5 bg-gradient-to-r ${s.gradient}`} />
                <div className="p-7">
                  <div className={`w-14 h-14 ${s.bg} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-105 transition-transform`}>
                    <span className="text-2xl">{s.emoji}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 font-outfit mb-0.5">{s.title}</h3>
                  <p className="text-sky-500 text-xs font-semibold mb-3">{s.subtitle}</p>
                  <p className="text-slate-500 text-sm leading-relaxed whitespace-pre-line mb-6 line-clamp-4">{s.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sky-500 font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      Explore <ChevronRight size={15} />
                    </span>
                    {familyStatus !== null && (
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${familyStatus ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {familyStatus ? 'Complete' : 'Pending'}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Tip banner */}
        <div className="mt-8 px-6 py-4 bg-gradient-to-r from-sky-50 to-blue-50 rounded-3xl border border-sky-100/60">
          <p className="text-sky-800 text-sm">
            <span className="font-bold">Tip:</span> Start with <strong>My Family</strong> to add your spouse and children — this unlocks access to Camps and Coaching programs.
          </p>
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}
